
import React, { useEffect, useRef, useState } from "react";
import { createChart, CandlestickSeries, createSeriesMarkers } from "lightweight-charts";
import { toast } from "sonner";
import { IoClose } from "react-icons/io5";
import { removeUnderscore } from "helpers/removeUnderscore";

const TP_SLResults = ({ results }) => {
    const containerRef = useRef(null);
    const chartRef = useRef(null);
    const seriesRef = useRef(null);
    const markersInstanceRef = useRef(null);
    const customMarkersRef = useRef([]);
    const trades = results?.trade_history || [];
    const cycles = results?.cycle_details || {};
    const { backtest_results, cycle_analytics } = results || {};
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [backtestName, setBacktestName] = useState("");

    const handleSubmit = async () => {
        if (!backtestName.trim()) {
            toast.error("Please enter a backtest name.");
            return;
        }

        try {
            // Call your save API function here
            await onSave(backtestName);
            setIsModalOpen(false);
            setBacktestName("");
        } catch (error) {
            console.error("Error saving backtest:", error);
        }
    };


    const summaryCards = [
        {
            title: "Total PnL",
            pnl: backtest_results?.total_pnl?.toFixed(2),
            roi: backtest_results?.roi?.toFixed(2),
            color: "text-gray-300",
        },
        {
            title: "Executed Trades",
            value: backtest_results?.executed_trades ?? "-",
            color: "text-gray-300",
        },
        {
            title: "Price Change",
            value: (backtest_results?.price_change?.toFixed(2) + '%') || "-",
            color: "text-gray-300",
        },
    ];

    const cycleCards = [
        {
            title: "Total Cycles",
            value: cycle_analytics?.total_cycles ?? "-",
            color: "text-gray-300",
        },
        {
            title: "Completed Cycles",
            value: cycle_analytics?.completed_cycles ?? "-",
            color: "text-gray-300",
        },
        {
            title: "Profitable Cycles",
            value: cycle_analytics?.profitable_cycles ?? "-",
            color: "text-gray-300",
        },
        {
            title: "Cycle Win Rate",
            value: (cycle_analytics?.cycle_win_rate?.toFixed(2) + '%') || "-",
            color: "text-gray-300",
        },
    ];


    // Initialize chart
    useEffect(() => {
        if (!containerRef.current) return;

        const chart = createChart(containerRef.current, {
            width: containerRef.current.clientWidth,
            height: 400,
            layout: {
                background: { color: "#1e1e1e" },
                textColor: "#ffffff",
            },
            grid: {
                vertLines: { color: "#333" },
                horzLines: { color: "#333" },
            },
            rightPriceScale: {
                borderColor: "#555",
                textColor: "#fff",
                scaleMargins: { top: 0.2, bottom: 0.1 },
            },
            timeScale: {
                timeVisible: true,
                borderColor: "#555",
            },
            crosshair: {
                mode: 0,
                horzLine: { color: "#888" },
                vertLine: { color: "#888" },
            },
        });

        chartRef.current = chart;

        // Add Candlestick series (v5)
        const series = chart.addSeries(CandlestickSeries, {
            upColor: "#26a69a",
            downColor: "#ef5350",
            borderVisible: false,
            wickUpColor: "#26a69a",
            wickDownColor: "#ef5350",
            borderUpColor: "#26a69a",
            borderDownColor: "#ef5350",
        });
        seriesRef.current = series;

        // Tooltip element
        const tooltip = document.createElement("div");
        tooltip.style.position = "absolute";
        tooltip.style.background = "rgba(0,0,0,0.8)";
        tooltip.style.color = "#fff";
        tooltip.style.padding = "6px";
        tooltip.style.borderRadius = "4px";
        tooltip.style.pointerEvents = "none";
        tooltip.style.zIndex = "1000";
        containerRef.current.appendChild(tooltip);

        chart.subscribeCrosshairMove((param) => {
            if (!param.time || !param.seriesData) {
                tooltip.style.display = "none";
                return;
            }

            const data = param.seriesData.get(series);
            if (!data) {
                tooltip.style.display = "none";
                return;
            }

            const markers = customMarkersRef.current;
            const marker = markers.find((m) => m.time === data.time);

            if (marker) {
                tooltip.innerHTML = `
          Price: ${marker.price}<br>
          Side: ${marker.side}<br>
          Action: ${marker.action}<br>
          Cycle: ${marker.cycle}
        `;
                tooltip.style.display = "block";
                tooltip.style.left = `${param.point.x + 10}px`;
                tooltip.style.top = `${param.point.y + 10}px`;
            } else {
                tooltip.style.display = "none";
            }
        });

        // Handle responsive
        const handleResize = () => {
            if (chartRef.current && containerRef.current) {
                chartRef.current.applyOptions({ width: containerRef.current.clientWidth });
            }
        };
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
            if (chartRef.current) chartRef.current.remove();
            if (tooltip.parentNode) tooltip.parentNode.removeChild(tooltip);
        };
    }, []);

    // Set OHLC data and trade markers
    useEffect(() => {
        if (!results?.ohlc_data || !seriesRef.current) return;

        // Format OHLC data
        const ohlc = results.ohlc_data.map((d) => ({
            time: Math.floor(d.timestamp / 1000),
            open: parseFloat(d.open.toFixed(2)),
            high: parseFloat(d.high.toFixed(2)),
            low: parseFloat(d.low.toFixed(2)),
            close: parseFloat(d.close.toFixed(2)),
        }));

        seriesRef.current.setData(ohlc);

        // Map trade_history to markers
        // Map trade_history to markers
        const markers = results.trade_history
            .map((trade) => {
                const tradeTimestamp = Math.floor(new Date(trade.date).getTime() / 1000);
                let candle = ohlc.reduce((closest, current) => {
                    const currentDiff = Math.abs(current.time - tradeTimestamp);
                    const closestDiff = Math.abs(closest.time - tradeTimestamp);
                    return currentDiff < closestDiff ? current : closest;
                }, ohlc[0]);

                if (!candle || Math.abs(candle.time - tradeTimestamp) > 3600) {
                    candle = ohlc.find(
                        (c) => trade.price >= Math.min(c.open, c.close) && trade.price <= Math.max(c.open, c.close)
                    );
                }

                if (!candle) return null;

                // Determine marker color based on action & side
                // Determine marker color and shape
                let color, shape, position;

                if (trade.action === "ENTRY") {
                    color = trade.side === "LONG" ? "#FFD700" : "#00BFFF"; // Gold for LONG, Blue for SHORT
                    shape = "arrowUp";
                    position = "belowBar";
                } else if (trade.action === "TAKE_PROFIT") {
                    color = "#00FF00"; // Bright green
                    shape = "arrowUp";
                    position = "aboveBar";
                } else if (trade.action === "STOP_LOSS") {
                    color = "#FF4C4C"; // Bright red
                    shape = "arrowDown"; // Use square instead of cross
                    position = "aboveBar";
                }



                return {
                    time: candle.time,
                    position,
                    shape,
                    color,
                    price: trade.price,
                    side: trade.side,
                    action: trade.action,
                    cycle: trade.cycle,
                    text: `${trade.side} ${trade.action} (Cycle ${trade.cycle})`,
                };

            })
            .filter(Boolean);

        // v5: Use createSeriesMarkers for markers
        if (!markersInstanceRef.current) {
            markersInstanceRef.current = createSeriesMarkers(seriesRef.current, markers);
        } else {
            markersInstanceRef.current.setMarkers(markers);
        }
        customMarkersRef.current = markers; // Store for tooltip

    }, [results]);


    if (!results) {
        // Modern loader
        return (
            <div className="flex flex-col items-center justify-center h-64">
                <div className="w-16 h-16 border-4 border-t-4 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
                <p className="mt-4 text-gray-400 text-lg">Loading backtest results...</p>
            </div>
        );
    }
    if (results.length === 0) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-gray-400">No backtest results available.</p>
            </div>
        );
    }

    return (

        <>
            <div className="space-y-4 p-2 bg-secondary">
                <div className="flex lg:flex-col xl:flex-row flex-col justify-center   gap-6 ">
                    {/* Backtest Summary + Cycle Analytics side by side */}
                    {[summaryCards, cycleCards].map((cards, sectionIdx) => (
                        <section key={sectionIdx}>
                            <div className="flex lg:grid lg:grid-cols-4 flex-col gap-4">
                                {cards.map((card, idx) => (
                                    <div
                                        key={idx}
                                        className="relative  border-[0.5px] border-gray-500
                       px-4 py-2 rounded-2xl shadow-xl hover:shadow-2xl hover:scale-105 
                       transform transition-all duration-300 cursor-pointer overflow-hidden"
                                    >
                                        {/* Decorative top-right blur */}
                                        {/* <div className="absolute top-0 right-0 w-16 h-16   rounded-full opacity-20 blur-2xl"></div> */}

                                        <div className="flex flex-col justify-center items-center gap-2">
                                            <div className={`text-sm  ${card.color} `}>
                                                {card.title}:
                                            </div>


                                            {card.title === "Total PnL" ? (
                                                <div className="flex items-baseline gap-2">
                                                    <span className="text-xs text-white">${card.pnl}</span>
                                                    {card.roi && (
                                                        <span
                                                            className={`text-xs font-medium px-2 py-1 rounded-full ${card.roi >= 0
                                                                    ? "text-green-500 bg-white/10"
                                                                    : "text-red-500 bg-white/10"
                                                                }`}
                                                        >
                                                            {card.roi}%
                                                        </span>

                                                    )}
                                                </div>
                                            ) : (
                                                <div className="text-xs  text-white truncate">
                                                    {card.value}
                                                </div>
                                            )}
                                        </div>
                                        {/* <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-16 h-1 bg-primary rounded-full opacity-40 blur-sm"></div> */}
                                    </div>
                                ))}
                            </div>
                        </section>
                    ))}
                </div>


                {/* Chart container */}
                <div className="p-4  rounded-2xl shadow-lg">
                    <div
                        className="glass rounded-xl p-2 relative"
                        style={{ width: "100%", height: 400 }}
                    >
                        <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
                    </div>
                </div>

                <div className="bg-secondary text-white p-6 rounded-2xl shadow-lg border border-gray-700">
                    <h2 className="text-xl font-semibold mb-4">Cycle Details</h2>

                    <div className="overflow-x-auto rounded-lg border border-gray-700">
                        <table className="min-w-full divide-y divide-gray-700">
                            <thead className="bg-gray-800/60">
                                <tr>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 uppercase tracking-wider">
                                        #
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 uppercase tracking-wider">
                                        StartDate
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 uppercase tracking-wider">
                                        EndDate
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 uppercase tracking-wider">
                                        Cycle
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 uppercase tracking-wider">
                                        End Price
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 uppercase tracking-wider">
                                        PNL
                                    </th>

                                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-300 uppercase tracking-wider">
                                        Trades
                                    </th>
                                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-300 uppercase tracking-wider">
                                        Completed
                                    </th>

                                </tr>
                            </thead>

                            <tbody className="divide-y divide-gray-700">
                                {cycles?.map((trade, index) => (
                                    <tr
                                        key={index}
                                        className="hover:bg-gray-800/50 transition-all duration-200"
                                    >
                                        <td className="px-4 py-3 text-sm text-gray-400">{index + 1}</td>
                                        <td className="px-4 py-3 text-sm">{trade.start_date}</td>
                                        <td className="px-4 py-3 text-sm">{trade.end_date}</td>

                                        <td className="px-4 py-3 text-sm text-gray-300">{trade.cycle}</td>

                                        <td className="px-4 py-3 text-sm text-gray-300">{trade.end_price?.toFixed(2)}</td>

                                        <td className="px-4 py-3 text-sm text-gray-300">
                                            {trade.pnl?.toFixed(3)}
                                        </td>

                                        <td className="px-4 py-3 text-sm text-right text-gray-200">
                                            {trade.trades}
                                        </td>

                                        <td className="px-4 py-3 text-sm text-right text-gray-200">
                                            {trade.completed ? "Yes" : "No"}
                                        </td>






                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>


                <div className="bg-secondary text-white p-6 rounded-2xl shadow-lg border border-gray-700">
                    <h2 className="text-xl font-semibold mb-4">Trade History</h2>

                    <div className="overflow-x-auto rounded-lg border border-gray-700">
                        <table className="min-w-full divide-y divide-gray-700">
                            <thead className="bg-gray-800/60">
                                <tr>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 uppercase tracking-wider">
                                        #
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 uppercase tracking-wider">
                                        Date
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 uppercase tracking-wider">
                                        Cycle
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 uppercase tracking-wider">
                                        Side
                                    </th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 uppercase tracking-wider">
                                        Action
                                    </th>
                                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-300 uppercase tracking-wider">
                                        Price
                                    </th>
                                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-300 uppercase tracking-wider">
                                        Quantity
                                    </th>
                                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-300 uppercase tracking-wider">
                                        PnL ($)
                                    </th>
                                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-300 uppercase tracking-wider">
                                        Fee ($)
                                    </th>
                                </tr>
                            </thead>

                            <tbody className="divide-y divide-gray-700">
                                {trades?.map((trade, index) => (
                                    <tr
                                        key={index}
                                        className="hover:bg-gray-800/50 transition-all duration-200"
                                    >
                                        <td className="px-4 py-3 text-sm text-gray-400">{index + 1}</td>
                                        <td className="px-4 py-3 text-sm">{trade.date}</td>
                                        <td className="px-4 py-3 text-sm text-gray-300">{trade.cycle}</td>

                                        <td
                                            className={`px-4 py-3 text-sm font-semibold ${trade.side === "LONG"
                                                ? "text-green-400"
                                                : trade.side === "SHORT"
                                                    ? "text-red-400"
                                                    : "text-gray-300"
                                                }`}
                                        >
                                            {trade.side}
                                        </td>

                                        <td className="px-4 py-3 text-sm text-gray-300">
                                            {removeUnderscore(trade.action)}
                                        </td>

                                        <td className="px-4 py-3 text-sm text-right text-gray-200">
                                            $ {trade.price?.toFixed(2)}
                                        </td>

                                        <td className="px-4 py-3 text-sm text-right text-gray-200">
                                            {trade.quantity?.toFixed(6)}
                                        </td>

                                        <td
                                            className={`px-4 py-3 text-sm text-right font-medium ${trade.pnl > 0
                                                ? "text-green-400"
                                                : trade.pnl < 0
                                                    ? "text-red-400"
                                                    : "text-gray-300"
                                                }`}
                                        >
                                            {trade.pnl?.toFixed(2)}
                                        </td>

                                        <td className="px-4 py-3 text-sm text-right text-gray-300">
                                            {trade.fee?.toFixed(2)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>


            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50">
                    <div className="bg-gray-800 text-white rounded-2xl shadow-lg w-11/12 sm:w-[400px] p-6 relative">
                        {/* Close Button */}
                        <button
                            onClick={() => setIsModalOpen(false)}
                            className="absolute top-3 right-3 text-gray-400 hover:text-white"
                        >
                            <IoClose size={22} />
                        </button>

                        {/* Modal Title */}
                        <h3 className="text-xl font-semibold mb-4 text-center">
                            Save Backtest
                        </h3>

                        {/* Input */}
                        <div className="mb-5">
                            <label className="block text-sm text-gray-300 mb-2">
                                Backtest Name
                            </label>
                            <input
                                type="text"
                                value={backtestName}
                                onChange={(e) => setBacktestName(e.target.value)}
                                placeholder="Enter backtest name"
                                className="w-full px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                        </div>

                        {/* Submit */}
                        <button
                            onClick={handleSubmit}
                            className="w-full bg-primary text-secondary py-2 rounded-xl font-semibold transition-all"
                        >
                            Save
                        </button>
                    </div>
                </div>
            )}
        </>

    );
};

export default TP_SLResults;